import {
    Alert,
    Box,
    Card,
    CardContent,
    CircularProgress,
    LinearProgress,
    Stack,
    Typography,
} from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import InfoHint from './InfoHint';
import useRequest from '../../hooks/useRequest';
import type {
    SalaryHoliday,
    SalaryScheduleEntry,
    SalaryShift,
    SalarySummary,
} from '../../domain/salary/types';

interface ApiResponse<T> {
    success: boolean;
    data: T;
}

const isoDateUtc = (date: Date): string => {
    const y = date.getUTCFullYear();
    const m = String(date.getUTCMonth() + 1).padStart(2, '0');
    const d = String(date.getUTCDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
};

const currentWeekIsoDates = (): string[] => {
    const today = new Date();
    const utc = new Date(Date.UTC(today.getFullYear(), today.getMonth(), today.getDate()));
    const dow = (utc.getUTCDay() + 6) % 7;
    const monday = new Date(utc);
    monday.setUTCDate(utc.getUTCDate() - dow);
    const days: string[] = [];
    for (let index = 0; index < 7; index += 1) {
        const day = new Date(monday);
        day.setUTCDate(monday.getUTCDate() + index);
        days.push(isoDateUtc(day));
    }
    return days;
};

const isWeekendIso = (iso: string): boolean => {
    const [y, m, d] = iso.split('-').map(Number);
    const dow = new Date(Date.UTC(y, m - 1, d)).getUTCDay();
    return dow === 0 || dow === 6;
};

const findMondayConflicts = (
    schedule: SalaryScheduleEntry[],
    weekendByEmployee: Map<number, string[]>,
    translate: (key: string, options?: Record<string, unknown>) => string,
): string[] => {
    const conflicts: string[] = [];
    for (const entry of schedule) {
        const [y, m, d] = entry.date.split('-').map(Number);
        const date = new Date(Date.UTC(y, m - 1, d));
        if (date.getUTCDay() !== 1) continue;
        const weekends = weekendByEmployee.get(entry.employeeId) ?? [];
        for (const wknd of weekends) {
            const [wy, wm, wd] = wknd.split('-').map(Number);
            const wdate = new Date(Date.UTC(wy, wm - 1, wd));
            const diff = (date.getTime() - wdate.getTime()) / 86_400_000;
            if (diff === 1 || diff === 2) {
                conflicts.push(
                    translate('salary.dashboard.warningMondayConflict', { date: entry.date }),
                );
                break;
            }
        }
    }
    return conflicts;
};

const DashboardTab = () => {
    const { t } = useTranslation();
    const request = useRequest();
    const [summary, setSummary] = useState<SalarySummary | null>(null);
    const [shifts, setShifts] = useState<SalaryShift[]>([]);
    const [holidays, setHolidays] = useState<SalaryHoliday[]>([]);
    const [schedule, setSchedule] = useState<SalaryScheduleEntry[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            setLoading(true);
            try {
                const now = new Date();
                const [summaryRes, shiftsRes, holidaysRes, scheduleRes] = await Promise.all([
                    request<ApiResponse<SalarySummary>>({ path: '/salary/summary' }),
                    request<ApiResponse<SalaryShift[]>>({ path: '/salary/shifts' }),
                    request<ApiResponse<SalaryHoliday[]>>({ path: '/salary/holidays' }),
                    request<ApiResponse<SalaryScheduleEntry[]>>({
                        path: '/salary/schedule',
                        params: {
                            year: now.getFullYear(),
                            month: now.getMonth() + 1,
                        },
                    }),
                ]);
                if (cancelled) return;
                setSummary(summaryRes.data);
                setShifts(shiftsRes.data ?? []);
                setHolidays(holidaysRes.data ?? []);
                setSchedule(scheduleRes.data ?? []);
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [request]);

    const coverage = useMemo(() => {
        if (shifts.length === 0) return { expected: 0, filled: 0 };
        const weekDays = currentWeekIsoDates();
        const weekendShifts = shifts.filter(s => s.isWeekendVariant);
        const weekdayShifts = shifts.filter(s => !s.isWeekendVariant);
        let expected = 0;
        const scheduleSet = new Set(schedule.map(entry => `${entry.date}:${entry.shiftId}`));
        const filledKeys = new Set<string>();
        for (const date of weekDays) {
            const dayShifts =
                isWeekendIso(date) && weekendShifts.length > 0 ? weekendShifts : weekdayShifts;
            expected += dayShifts.length;
            for (const shift of dayShifts) {
                const key = `${date}:${shift.id}`;
                if (scheduleSet.has(key)) filledKeys.add(key);
            }
        }
        return { expected, filled: filledKeys.size };
    }, [shifts, schedule]);

    const warnings = useMemo(() => {
        const list: string[] = [];
        const weekendByEmployee = new Map<number, string[]>();
        for (const entry of schedule) {
            if (!isWeekendIso(entry.date)) continue;
            const arr = weekendByEmployee.get(entry.employeeId) ?? [];
            arr.push(entry.date);
            weekendByEmployee.set(entry.employeeId, arr);
        }
        list.push(...findMondayConflicts(schedule, weekendByEmployee, t));
        if (coverage.expected > 0 && coverage.filled < coverage.expected) {
            list.push(
                t('salary.dashboard.warningCoverage', {
                    missing: coverage.expected - coverage.filled,
                }),
            );
        }
        const today = new Date();
        const todayIso = isoDateUtc(
            new Date(Date.UTC(today.getFullYear(), today.getMonth(), today.getDate())),
        );
        const weekEnd = new Date(
            Date.UTC(today.getFullYear(), today.getMonth(), today.getDate() + 7),
        );
        const weekEndIso = isoDateUtc(weekEnd);
        for (const holiday of holidays) {
            if (holiday.date >= todayIso && holiday.date <= weekEndIso) {
                list.push(
                    t('salary.dashboard.warningHoliday', {
                        name: holiday.name,
                        date: holiday.date,
                    }),
                );
            }
        }
        return list;
    }, [schedule, holidays, coverage, t]);

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" py={4}>
                <CircularProgress />
            </Box>
        );
    }
    if (!summary) {
        return <Typography>{t('salary.dashboard.noData')}</Typography>;
    }

    const formatCurrency = (value: number) =>
        `${Math.round(value).toLocaleString()} ${t('salary.currency')}`;

    const coveragePercent =
        coverage.expected > 0 ? Math.round((coverage.filled / coverage.expected) * 100) : 0;

    return (
        <Stack spacing={2}>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} flexWrap="wrap">
                <Card sx={{ flex: 1, minWidth: 220 }}>
                    <CardContent>
                        <Typography variant="body2" color="text.secondary">
                            {t('salary.dashboard.totalEmployees')}
                            <InfoHint title={t('salary.info.dashboard.totalEmployees')} />
                        </Typography>
                        <Typography variant="h5">{summary.totalEmployees}</Typography>
                        <Typography variant="caption">
                            {t('salary.dashboard.activeCount', { count: summary.activeEmployees })}
                        </Typography>
                    </CardContent>
                </Card>
                <Card sx={{ flex: 1, minWidth: 220 }}>
                    <CardContent>
                        <Typography variant="body2" color="text.secondary">
                            {t('salary.dashboard.monthlyGross')}
                            <InfoHint title={t('salary.info.dashboard.monthlyGross')} />
                        </Typography>
                        <Typography variant="h5">{formatCurrency(summary.totalGross)}</Typography>
                    </CardContent>
                </Card>
                <Card sx={{ flex: 1, minWidth: 220 }}>
                    <CardContent>
                        <Typography variant="body2" color="text.secondary">
                            {t('salary.dashboard.monthlyTaxes')}
                            <InfoHint title={t('salary.info.dashboard.monthlyTaxes')} />
                        </Typography>
                        <Typography variant="h5">{formatCurrency(summary.totalTaxes)}</Typography>
                    </CardContent>
                </Card>
                <Card sx={{ flex: 1, minWidth: 220 }}>
                    <CardContent>
                        <Typography variant="body2" color="text.secondary">
                            {t('salary.dashboard.monthlyNet')}
                            <InfoHint title={t('salary.info.dashboard.monthlyNet')} />
                        </Typography>
                        <Typography variant="h5">{formatCurrency(summary.totalNet)}</Typography>
                    </CardContent>
                </Card>
            </Stack>

            <Card>
                <CardContent>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                        {t('salary.dashboard.weeklyCoverage')}
                        <InfoHint title={t('salary.info.dashboard.weeklyCoverage')} />
                    </Typography>
                    <Stack direction="row" alignItems="center" spacing={2}>
                        <Typography variant="h6">
                            {coverage.filled} / {coverage.expected}
                        </Typography>
                        <Box sx={{ flex: 1 }}>
                            <LinearProgress
                                variant="determinate"
                                value={coveragePercent}
                                color={coveragePercent >= 90 ? 'success' : 'warning'}
                            />
                        </Box>
                        <Typography variant="body2">{coveragePercent}%</Typography>
                    </Stack>
                </CardContent>
            </Card>

            {warnings.length > 0 && (
                <Stack spacing={1}>
                    {warnings.map(message => (
                        <Alert
                            key={message}
                            severity="warning"
                            icon={<WarningAmberIcon fontSize="inherit" />}
                        >
                            {message}
                        </Alert>
                    ))}
                </Stack>
            )}
        </Stack>
    );
};

export default DashboardTab;
